import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ChatMessage, Agent } from '../types/mundus';
import { sendMessage, getMessages } from '../lib/messages.api';
import { handleApiError } from '../lib/error-handler';
import { ChatSkeleton } from './Skeleton';

interface AgentStep {
  type: 'text' | 'tool_use' | 'tool_result';
  content?: string;
  toolName?: string;
  toolInput?: any;
  toolResult?: string;
  success?: boolean;
}

interface QuickAction {
  id: string;
  label: string;
  message: string;
}

const TOOL_ICONS: Record<string, string> = {
  send_whatsapp: '💬',
  read_inbox: '📧',
  send_email: '📧',
  list_calendar_events: '📅',
  create_calendar_event: '📅',
  create_task: '✅',
  list_tasks: '📋',
  complete_task: '✓',
  web_search: '🔍',
};

const TOOL_LABELS: Record<string, string> = {
  send_whatsapp: 'WhatsApp',
  read_inbox: 'Reading inbox',
  send_email: 'Sending email',
  list_calendar_events: 'Checking calendar',
  create_calendar_event: 'Creating event',
  create_task: 'Creating task',
  list_tasks: 'Listing tasks',
  complete_task: 'Completing task',
  web_search: 'Searching web',
};

const FALLBACK_ACTIONS: QuickAction[] = [
  { id: 'fallback_task', label: 'Create a task', message: 'Help me create a new task' },
  { id: 'fallback_plan', label: 'Plan my day', message: 'Help me plan my priorities for today' },
  { id: 'fallback_progress', label: 'Check progress', message: 'Show me my current progress and wins' },
];

interface ChatProps {
  mundusId: string;
  agents: Agent[];
  colors?: {
    border: string;
    glow: string;
    text: string;
    bg: string;
  };
}

export default function Chat({ mundusId, agents }: ChatProps) {
  useTranslation(); // i18n init
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [quickActions, setQuickActions] = useState<QuickAction[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  void agents; // Agent data available but we use fixed "Atento" branding

  useEffect(() => {
    loadMessages();
  }, [mundusId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessages = async () => {
    setIsLoading(true);
    try {
      const fetched = await getMessages(mundusId);
      setMessages(fetched || []);
      // Load quickActions from the last assistant message
      if (fetched?.length) {
        const lastAssistant = [...fetched].reverse().find((m: ChatMessage) => m.role === 'ASSISTANT');
        const actions = (lastAssistant as any)?.metadata?.quickActions || [];
        setQuickActions(actions);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || isSending) return;

    await doSendMessage(inputMessage.trim());
  };

  const doSendMessage = async (content: string) => {
    setInputMessage('');
    setQuickActions([]); // Clear previous actions while sending
    setIsSending(true);

    try {
      const response = await sendMessage(mundusId, { content });
      setMessages(prev => [
        ...prev,
        response.data.userMessage,
        response.data.assistantMessage,
      ]);
      // Extract quickActions from response metadata, with fallback defaults
      const actions: QuickAction[] = response.data.assistantMessage?.metadata?.quickActions || [];
      if (actions.length > 0) {
        setQuickActions(actions);
      } else {
        // Fallback quick actions when agent doesn't suggest any
        setQuickActions(FALLBACK_ACTIONS);
      }

      // Check for win completions in tool steps and celebrate
      const steps: AgentStep[] = response.data.assistantMessage?.metadata?.steps || [];
      for (const step of steps) {
        if (step.type === 'tool_result' && step.success && step.toolName === 'complete_task') {
          toast.success('Task completed! Keep going to earn your win.', { icon: '✅', duration: 3000 });
        }
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      handleApiError(error, { action: 'send message' });
      setInputMessage(content);
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  };

  const handleQuickAction = (action: QuickAction) => {
    if (isSending) return;
    doSendMessage(action.message);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  /** Render tool action cards from message metadata */
  const renderToolSteps = (message: ChatMessage) => {
    const steps: AgentStep[] = (message as any).metadata?.steps || [];
    // Filter out suggest_actions (rendered as chips, not as tool cards)
    const toolSteps = steps.filter(s => (s.type === 'tool_use' || s.type === 'tool_result') && s.toolName !== 'suggest_actions');

    if (toolSteps.length === 0) return null;

    // Group tool_use + tool_result pairs
    const pairs: { use: AgentStep; result?: AgentStep }[] = [];
    for (let i = 0; i < toolSteps.length; i++) {
      if (toolSteps[i].type === 'tool_use') {
        const result = toolSteps[i + 1]?.type === 'tool_result' ? toolSteps[i + 1] : undefined;
        pairs.push({ use: toolSteps[i], result });
        if (result) i++;
      }
    }

    return (
      <div className="space-y-2 mb-2">
        {pairs.map((pair, i) => {
          const icon = TOOL_ICONS[pair.use.toolName || ''] || '🔧';
          const label = TOOL_LABELS[pair.use.toolName || ''] || pair.use.toolName;
          const success = pair.result?.success !== false;

          return (
            <div
              key={i}
              className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${
                success
                  ? 'bg-blue-50 border border-blue-100 text-blue-800'
                  : 'bg-red-50 border border-red-100 text-red-800'
              }`}
            >
              <span className="text-base mt-0.5">{icon}</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{label}</div>
                {pair.result?.toolResult && (
                  <div className="text-xs opacity-75 mt-0.5 truncate">
                    {pair.result.toolResult.substring(0, 120)}
                  </div>
                )}
              </div>
              <span className="text-base">{success ? '✓' : '✗'}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col h-[600px]">
      {/* Header */}
      <div className="border-b border-slate-200 p-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold">
            A
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Atento</h3>
            <p className="text-xs text-slate-500">Your AI agent</p>
          </div>
          {isSending && (
            <div className="ml-auto flex items-center gap-1 text-xs text-blue-500">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
              Thinking...
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
        {isLoading ? (
          <ChatSkeleton />
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold mb-4">
              A
            </div>
            <p className="text-slate-700 font-medium mb-1">
              Hi, I'm Atento
            </p>
            <p className="text-slate-400 text-sm max-w-sm mb-4">
              I can create tasks, send WhatsApp messages, manage your calendar, and help you plan your goals.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {[
                { id: 'start_plan', label: 'Plan my day', message: 'Help me plan my day and set priorities' },
                { id: 'start_tasks', label: 'Create a task', message: 'I want to create a new task' },
                { id: 'start_goals', label: 'Set a goal', message: 'Help me set a new goal and break it into steps' },
              ].map((action) => (
                <button
                  key={action.id}
                  onClick={() => handleQuickAction(action)}
                  disabled={isSending}
                  className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full hover:bg-blue-100 hover:border-blue-300 transition-colors active:scale-95 disabled:opacity-50"
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'USER' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[85%] ${message.role === 'USER' ? '' : ''}`}>
                  {/* Tool action cards (for assistant messages) */}
                  {message.role === 'ASSISTANT' && renderToolSteps(message)}

                  {/* Message bubble */}
                  <div
                    className={`rounded-xl px-4 py-3 ${
                      message.role === 'USER'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-slate-200 text-slate-900'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    <div className={`text-xs mt-1 ${message.role === 'USER' ? 'text-blue-200' : 'text-slate-400'}`}>
                      {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Quick Action Chips */}
      {quickActions.length > 0 && !isSending && (
        <div className="px-3 py-2 bg-white border-t border-slate-100 flex flex-wrap gap-2">
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={() => handleQuickAction(action)}
              className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full hover:bg-blue-100 hover:border-blue-300 transition-colors active:scale-95"
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSendMessage} className="border-t border-slate-200 p-3 bg-white">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask me to do something..."
            className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={2}
            disabled={isSending}
          />
          <button
            type="submit"
            disabled={!inputMessage.trim() || isSending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors self-end"
          >
            {isSending ? '...' : '→'}
          </button>
        </div>
      </form>
    </div>
  );
}
